import { useState, useMemo } from "react";
import { ToolsShell, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Coverage Calculator — area × depth/coverage → amount of material needed.
// Multiple material modes with sensible defaults. Includes a waste-factor
// adjustment (industry-standard 10% default for most materials).
//
// All formulas are in metric internally; UI converts to/from imperial.

const MATERIALS = {
  concrete: {
    label: "Concrete",
    coverageLabel: "Thickness",
    coverageUnit: { imperial: "in", metric: "cm" },
    coverageDefault: { imperial: "4", metric: "10" },
    output: "volume",
    bagYield: { imperial: { val: 0.6, unit: "cu ft" }, metric: { val: 0.017, unit: "m³" } },
    bagLabel: "80 lb bags",
    note: "An 80 lb bag yields about 0.6 cu ft of mixed concrete.",
  },
  paint: {
    label: "Paint",
    coverageLabel: "Coats",
    coverageUnit: null,
    coverageDefault: { imperial: "2", metric: "2" },
    output: "area",
    bagYield: { imperial: { val: 350, unit: "sq ft" }, metric: { val: 32, unit: "m²" } },
    bagLabel: "gallons (or 3.78 L cans)",
    note: "A gallon covers about 350 sq ft per coat on smooth interior walls; textured or porous surfaces use 25-30% more.",
  },
  tile: {
    label: "Tile",
    coverageLabel: "Tile size",
    coverageUnit: { imperial: "in (side)", metric: "cm (side)" },
    coverageDefault: { imperial: "12", metric: "30" },
    output: "count",
    bagYield: null,
    bagLabel: "tiles",
    note: "Always buy 10% extra for cuts, breakage, and future repairs.",
  },
  mulch: {
    label: "Mulch / Soil",
    coverageLabel: "Depth",
    coverageUnit: { imperial: "in", metric: "cm" },
    coverageDefault: { imperial: "3", metric: "7" },
    output: "volume",
    bagYield: { imperial: { val: 2, unit: "cu ft" }, metric: { val: 0.057, unit: "m³" } },
    bagLabel: "2 cu ft bags",
    note: "Standard bagged mulch is 2 cubic feet; bulk mulch is sold by cubic yards (1 yd³ = 27 ft³ = 13.5 bags).",
  },
  gravel: {
    label: "Gravel / Sand",
    coverageLabel: "Depth",
    coverageUnit: { imperial: "in", metric: "cm" },
    coverageDefault: { imperial: "2", metric: "5" },
    output: "volume",
    bagYield: { imperial: { val: 0.5, unit: "cu ft" }, metric: { val: 0.014, unit: "m³" } },
    bagLabel: "50 lb bags",
    note: "A 50 lb bag of pea gravel is about 0.5 cu ft. For driveways or large areas, bulk delivery is much cheaper per cubic yard.",
  },
  lumber: {
    label: "Lumber (board feet)",
    coverageLabel: "Board thickness",
    coverageUnit: { imperial: "in (nominal)", metric: "in (nominal)" },
    coverageDefault: { imperial: "1", metric: "1" },
    output: "boardfeet",
    bagYield: null,
    bagLabel: "board feet",
    note: "Board feet = (thickness in × width in × length ft) / 12. Lumber is priced per board-foot.",
  },
};

export default function CoverageCalculator() {
  const [material, setMaterial] = useState("concrete");
  const [units, setUnits] = useState("imperial"); // imperial | metric
  const [length, setLength] = useState("10");
  const [width, setWidth] = useState("10");
  const [coverage, setCoverage] = useState("4");
  const [waste, setWaste] = useState("10");

  // Recompute defaults when material changes
  const onMaterialChange = (m) => {
    setMaterial(m);
    setCoverage(MATERIALS[m].coverageDefault[units]);
  };
  const onUnitsChange = (u) => {
    setUnits(u);
    setCoverage(MATERIALS[material].coverageDefault[u]);
  };

  const mat = MATERIALS[material];
  const lenUnit = units === "imperial" ? "ft" : "m";
  const areaUnit = units === "imperial" ? "sq ft" : "m²";

  const result = useMemo(() => {
    const L = parseFloat(length);
    const W = parseFloat(width);
    const C = parseFloat(coverage);
    const wasteFactor = 1 + (parseFloat(waste) || 0) / 100;
    if (isNaN(L) || isNaN(W) || L <= 0 || W <= 0) return { err: "Enter length and width." };

    const area = L * W;

    if (mat.output === "area") {
      // Paint: area * coats, then divide by gallon-yield.
      const totalArea = area * (parseFloat(C) || 1) * wasteFactor;
      const cans = totalArea / mat.bagYield[units].val;
      return { area: totalArea, primary: cans, primaryLabel: mat.bagLabel, areaUnit };
    }
    if (mat.output === "volume") {
      // Volume = area * depth (consistent units).
      // Convert depth: imperial inches → feet, metric cm → m.
      const depthInMainUnit = units === "imperial" ? (C / 12) : (C / 100);
      const volume = area * depthInMainUnit * wasteFactor;
      const volUnit = units === "imperial" ? "cu ft" : "m³";
      const bags = volume / mat.bagYield[units].val;
      return { area, volume, volUnit, primary: bags, primaryLabel: mat.bagLabel };
    }
    if (mat.output === "count") {
      // Tile: area / tile area (tile is a square of side C).
      const sideInMainUnit = units === "imperial" ? (C / 12) : (C / 100);
      const tileArea = sideInMainUnit * sideInMainUnit;
      const tiles = (area / tileArea) * wasteFactor;
      return { area, primary: tiles, primaryLabel: mat.bagLabel };
    }
    if (mat.output === "boardfeet") {
      // Lumber: boardft = (thickness in × width in × length ft) / 12.
      // Treat L as length (ft), W as width (in), C as thickness (in).
      const bf = ((parseFloat(W) * parseFloat(C) * parseFloat(L)) / 12) * wasteFactor;
      return { primary: bf, primaryLabel: "board feet" };
    }
    return { err: "Unknown material" };
  }, [length, width, coverage, waste, material, units]);

  const sel = {
    width: "100%", background: "#13141F", border: "1px solid #262839",
    borderRadius: 10, color: "#E8E9F3", fontSize: 15, padding: "12px 14px",
    boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 13, color: "#9A9DBE", display: "block", marginBottom: 6, marginTop: 14 };

  return (
    <ToolsShell title="Coverage Calculator">
      <label style={labelStyle}>Material</label>
      <select value={material} onChange={(e) => onMaterialChange(e.target.value)} style={sel}>
        {Object.entries(MATERIALS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>

      <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
        {[{ id: "imperial", label: "Imperial" }, { id: "metric", label: "Metric" }].map((u) => (
          <button
            key={u.id}
            onClick={() => onUnitsChange(u.id)}
            style={{ background: units === u.id ? TOOLS_ACCENT : "#1C1E2E", color: units === u.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", flex: 1 }}
          >
            {u.label}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 4 }}>
        <div>
          <label style={labelStyle}>{material === "lumber" ? `Length (${lenUnit})` : `Length (${lenUnit})`}</label>
          <input value={length} onChange={(e) => setLength(e.target.value)} inputMode="decimal" style={sel} />
        </div>
        <div>
          <label style={labelStyle}>{material === "lumber" ? "Width (in nominal)" : `Width (${lenUnit})`}</label>
          <input value={width} onChange={(e) => setWidth(e.target.value)} inputMode="decimal" style={sel} />
        </div>
      </div>

      <label style={labelStyle}>
        {mat.coverageLabel}{mat.coverageUnit ? ` (${mat.coverageUnit[units]})` : ""}
      </label>
      <input value={coverage} onChange={(e) => setCoverage(e.target.value)} inputMode="decimal" style={sel} />

      <label style={labelStyle}>Waste factor (%)</label>
      <input value={waste} onChange={(e) => setWaste(e.target.value)} inputMode="decimal" style={sel} />

      <div style={{ marginTop: 22, background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "18px 20px" }}>
        {result.err ? (
          <div style={{ color: "#F8716A", fontSize: 14 }}>{result.err}</div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 6 }}>You need approximately</div>
            <div style={{ fontSize: 38, fontWeight: 800, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
              {Math.ceil(result.primary)} <span style={{ fontSize: 16, fontWeight: 500, color: "#9A9DBE" }}>{result.primaryLabel}</span>
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: "#7C8099" }}>
              ({(result.primary).toFixed(2)} {result.primaryLabel}, rounded up)
            </div>
            {result.area !== undefined && (
              <div style={{ marginTop: 10, fontSize: 13, color: "#C8CADF" }}>
                Area: {result.area.toFixed(1)} {result.areaUnit || areaUnit}
                {result.volume !== undefined && ` · Volume: ${result.volume.toFixed(2)} ${result.volUnit}`}
              </div>
            )}
          </>
        )}
      </div>

      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 16 }}>
        {mat.note}
      </p>
    </ToolsShell>
  );
}
